import React, { useEffect } from "react";

import { BaseTile } from "Components/BaseTile";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useUserIdentification } from "Features/CheckPrice/userIdentificationHook";
import { pageDisplayed } from "Store/analytics/analyticsSlice";
import { useCurrentCart } from "Store/cart";
import { useBrandingTheme } from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { FormState } from "Store/priceCalculation/priceCalculationInterfaces";
import { updateHuseierneMembershipStatusStart } from "Store/updateHuseierneMembershipStatus/slice";
import { AppPartName, stepName } from "Utils/analytics";
import { useScrollToTop } from "Utils/autoScrollUtils";
import { FormikChildren } from "Utils/types";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import {
  withPartnerInitialValues,
  withPartnerValidationSchema,
} from "../partnerValidation";

interface Props extends FormikChildren {
  readonly header: string;
  readonly intro?: string;
  readonly onSubmit?: (formValues?: any) => void;
  readonly validationSchema?: any | (() => any);
  readonly initialValues?: any;
  readonly withPartnerValidation?: boolean;
}

export const ProductDetailsTile = ({
  header,
  intro,
  children,
  onSubmit,
  initialValues,
  validationSchema,
  withPartnerValidation = false,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const currentCart = useCurrentCart();
  const userIdentificationEntity = useUserIdentification(
    ProductBackendName.HOUSE,
  );
  const { id: partnerId } = useBrandingTheme();
  useScrollToTop();
  useEffect(() => {
    dispatch(pageDisplayed({ pageName: stepName(2, AppPartName.CHECK_PRICE) }));
  }, []);
  const handleSubmit = (formValues: FormState): void => {
    if (
      partnerId === Partner.HUSEIERNE &&
      "huseierneMembershipStatus" in formValues
    ) {
      dispatch(
        updateHuseierneMembershipStatusStart({
          customerRef:
            userIdentificationEntity.current?.newCustomerResponse.customerRef ??
            "",
          huseierneMembershipStatus: formValues["huseierneMembershipStatus"],
        }),
      );
    }
    if (onSubmit) {
      onSubmit(formValues);
    }
  };

  return (
    <BaseTile header={header} intro={intro}>
      <Formik
        initialValues={withPartnerInitialValues(
          partnerId,
          initialValues ?? {},
          currentCart,
          withPartnerValidation,
        )}
        enableReinitialize
        validationSchema={withPartnerValidationSchema(
          partnerId,
          validationSchema,
          withPartnerValidation,
        )}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {children}
      </Formik>
    </BaseTile>
  );
};
